import { Cross2Icon } from "@radix-ui/react-icons";
import { Box, Flex, IconButton, Select, Spinner, Text, Tooltip } from "@radix-ui/themes";
import { Control, Controller, FieldErrors, FieldValues, Path } from "react-hook-form";
import {
  ComposedName,
  FieldShell,
  fieldAria,
  fieldIds,
  resolveFieldError,
} from "../FieldShell";

interface SelectProps<T extends FieldValues> {
  name: Path<T>; // Agora restrito apenas a chaves de `T`
  label: string;
  control: Control<T>;
  errors: FieldErrors<T>;
  style?: any;
  fieldProps?: any;
  options?: { value: string; label: string }[];
  itemsLoading?: boolean;
  itemsError?: boolean;
  onSelect?: (value: string) => void;
  composedName?: ComposedName;
  required?: boolean;
  hint?: string;
  placeholder?: string;
}

const SelectComponent = <T extends FieldValues>({
  name,
  label,
  control,
  errors,
  style,
  fieldProps,
  options,
  onSelect,
  composedName,
  itemsError,
  itemsLoading,
  required,
  hint,
  placeholder = "Selecione uma opção",
}: SelectProps<T>) => {
  const error = resolveFieldError(errors, name, composedName);
  const { fieldId } = fieldIds(name);

  return (
    <Box {...style}>
      <FieldShell name={name} label={label} required={required} error={error} hint={hint}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const selected = options?.find((p) => p.value === field.value);
            const canClear = !!field.value && !fieldProps?.disabled;

            if (itemsError) {
              return (
                <Text as="p" size="1" color="red">
                  Erro ao carregar as opções.
                </Text>
              );
            }

            return (
              <Flex align="center" gap="1">
                <Select.Root
                  onValueChange={(value) => {
                    field.onChange(value);
                    if (onSelect) onSelect(value);
                  }}
                  value={field.value || ""}
                  {...fieldProps}
                >
                  <Select.Trigger
                    id={fieldId}
                    color={error ? "red" : undefined}
                    disabled={itemsLoading || fieldProps?.disabled}
                    {...fieldAria(name, error, hint)}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Flex align="center" gap="2">
                      {itemsLoading && <Spinner size="1" />}
                      <Text truncate color={selected ? undefined : "gray"}>
                        {itemsLoading ? "A carregar…" : selected?.label ?? placeholder}
                      </Text>
                    </Flex>
                  </Select.Trigger>

                  <Select.Content position="popper">
                    <Select.Group>
                      <Select.Label>{label}</Select.Label>
                      {options && options.length > 0 ? (
                        options.map((option) => (
                          <Select.Item key={option.value} value={option.value}>
                            {option.label}
                          </Select.Item>
                        ))
                      ) : (
                        <Box px="3" py="2">
                          <Text size="1" color="gray">
                            Sem opções disponíveis
                          </Text>
                        </Box>
                      )}
                    </Select.Group>
                  </Select.Content>
                </Select.Root>

                {canClear && (
                  <Tooltip content="Limpar selecção">
                    <IconButton
                      type="button"
                      variant="ghost"
                      color="gray"
                      size="1"
                      aria-label={`Limpar ${label}`}
                      onClick={() => {
                        field.onChange("");
                        if (onSelect) onSelect("");
                      }}
                    >
                      <Cross2Icon />
                    </IconButton>
                  </Tooltip>
                )}
              </Flex>
            );
          }}
        />
      </FieldShell>
    </Box>
  );
};

export default SelectComponent;
